import { css } from '@emotion/react';
import { Typography, LinearProgress, Paper } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import EnhancedTable, { PAGE_SIZE_OPTIONS } from '../Table/EnhancedTable';
import FilterBar, {
  SORT_OPTIONS,
  STATE_FILTERS,
  StateFilter,
  TASK_STATE_FILTERS,
  TaskStateFilterValue,
  nextStateFilter,
} from '../FilterBar';
import ErrorSummaryDialog from 'pages/DashboardPage/ErrorSummaryDialog';
import { useColumns } from './columns';
import CancelButton from './CancelButton';
import DeleteButton from './DeleteButton';
import StartButton from './StartButton';

import useAutofetchedRunsAsync from 'store/useAutofetchedRunsAsync';
import fetchRuns from 'store/fetchRuns';
import { IRunsParams } from 'store/buildRunsPath';
import { DashboardSortKey, DashboardSortOrder } from 'store/fetchDashboardRuns';
import useUsers from 'store/useUsers';
import useDebouncedSearchInput from 'hooks/useDebouncedSearchInput';
import useListStateParams, { booleanParam, oneOfParam, positiveIntParam, stringParam } from 'hooks/useListStateParams';

import { toggle, unique } from 'utils/array';

import { ACTIVITY_WINDOWS, ActivityWindow, DEFAULT_ACTIVITY_WINDOW } from 'types/RunState';
import IRunEntry from 'types/IRunEntry';

const listParams = {
  page: positiveIntParam(1),
  pageSize: oneOfParam(PAGE_SIZE_OPTIONS, 25),
  search: stringParam(),
  state: oneOfParam(STATE_FILTERS, ''),
  taskState: oneOfParam(TASK_STATE_FILTERS, ''),
  activityWindow: oneOfParam(ACTIVITY_WINDOWS, DEFAULT_ACTIVITY_WINDOW),
  user: stringParam(),
  running: booleanParam(),
  sort: oneOfParam(
    SORT_OPTIONS.map((option) => option.key),
    'created_on',
  ),
  order: oneOfParam<DashboardSortOrder>(['asc', 'desc'], 'desc'),
};

export default function Runs(props: {
  selectedRunIds: string[];
  setSelectedRunIds: Dispatch<SetStateAction<string[]>>;
}) {
  const { selectedRunIds, setSelectedRunIds } = props;

  const { state, update } = useListStateParams(listParams);
  const { page, pageSize } = state;
  const [errorSummaryRun, setErrorSummaryRun] = useState<IRunEntry | null>(null);

  const applySearch = useCallback((value: string) => update({ search: value, page: 1 }), [update]);
  const [search, handleSearchChange] = useDebouncedSearchInput(state.search, applySearch);

  const users = useUsers();

  const runsParams = useMemo<IRunsParams>(
    () => ({
      page,
      pageSize,
      search: state.search || undefined,
      state: state.state || undefined,
      taskState: state.taskState || undefined,
      updatedWithinDays: state.activityWindow || undefined,
      userId: state.user || undefined,
      hasRunningTasks: state.running || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [page, pageSize, state],
  );
  const runsAsync = useAutofetchedRunsAsync(runsParams);
  const refetchRuns = useCallback(() => fetchRuns(runsParams), [runsParams]);

  const { status } = runsAsync;
  const paginatedData = runsAsync.status === 'SUCCESS' || runsAsync.status === 'REFRESH' ? runsAsync.data : undefined;
  const runs = paginatedData?.items ?? [];
  const total = paginatedData?.total ?? 0;

  const handleStateClick = useCallback(
    (value: string) => {
      const next = nextStateFilter(state.state, value);
      if (next === null) return;
      update({ state: next, page: 1 });
    },
    [state.state, update],
  );

  const columns = useColumns(setErrorSummaryRun, {
    onStateClick: handleStateClick,
    selectedState: state.state,
  });

  const handlePageChange = useCallback((newPage: number) => update({ page: newPage }), [update]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => update({ pageSize: newPageSize, page: 1 }),
    [update],
  );

  return (
    <section>
      <Paper elevation={1} style={{ padding: 48 }}>
        <Typography component="h1" variant="h6">
          Workflows
        </Typography>
        <br />
        {status === 'ERROR' && <p>Something went wrong</p>}
        <FilterBar
          searchId="search-workflow-runs"
          search={search}
          onSearchChange={handleSearchChange}
          state={state.state}
          onStateChange={(value: StateFilter) => update({ state: value, page: 1 })}
          taskState={state.taskState}
          onTaskStateChange={(value: TaskStateFilterValue) => update({ taskState: value, page: 1 })}
          activityWindow={state.activityWindow}
          onActivityWindowChange={(value: ActivityWindow) => update({ activityWindow: value, page: 1 })}
          users={users}
          userId={state.user}
          onUserIdChange={(id) => update({ user: id, page: 1 })}
          hasRunningTasks={state.running}
          onHasRunningTasksChange={(value) => update({ running: value, page: 1 })}
          sort={state.sort}
          onSortChange={(key: DashboardSortKey) => update({ sort: key, page: 1 })}
          order={state.order}
          onOrderChange={(value: DashboardSortOrder) => update({ order: value, page: 1 })}
        />
        <br />
        {['INIT', 'PENDING'].includes(status) && <LinearProgress />}
        <EnhancedTable<IRunEntry>
          columns={columns}
          items={runs}
          selection={{
            isSelected: (runId: string) => selectedRunIds.includes(runId),
            selectOnly: (runId: string) => setSelectedRunIds([runId]),
            multi: {
              someSelected: runs.some((run) => selectedRunIds.includes(run.id)),
              allSelected: runs.length > 0 && runs.every((run) => selectedRunIds.includes(run.id)),
              toggle: (runId: string) => setSelectedRunIds(toggle(runId)),
              selectAll: () => setSelectedRunIds((ids) => ids.concat(runs.map((run) => run.id)).filter(unique)),
              deselectAll: () => setSelectedRunIds((ids) => ids.filter((id) => !runs.some((run) => run.id === id))),
            },
          }}
          serverPagination={{
            page,
            pageSize,
            total,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
        <br />
        <div
          css={css`
            display: flex;

            > * ~ * {
              margin-left: 16px;
            }
          `}
        >
          <DeleteButton selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))} />
          <CancelButton selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))} />
          <StartButton selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))} />
        </div>
      </Paper>
      {errorSummaryRun && (
        <ErrorSummaryDialog
          run={errorSummaryRun}
          onClose={() => setErrorSummaryRun(null)}
          onResetSuccess={refetchRuns}
        />
      )}
    </section>
  );
}
